import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Description as DescriptionIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  TaskAlt as TaskAltIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Logger } from 'utils/Logger';
import { TemplatesCreationGraph } from './TemplatesCreationGraph';
import { DatasetsCreationGraph } from './DatasetsCreationGraph';
import { CampaignsCreationGraph } from './CampaignsCreationGraph';
import { SchedulerStatsGraph } from './SchedulerStatsGraph';
import { WhatsappCampaignSummaryGraph } from './WhatsappCampaignSummaryGraph';
import { EmailCampaignSummaryGraph } from './EmailCampaignSummaryGraph';
import { apiClient } from 'utils/ApiClient';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';

interface DashboardStats {
  totalDatasets: number;
  activeDatasets: number;
  totalTemplates: number;
  totalCampaigns: number;
}

type TemplatesStats = { total: number; whatsApp: number; sms: number; email: number; telegram: number };
type DatasetsStats = { total: number; active: number; inactive: number; deleted: number };
type CampaignsStats = {
  total: number;
  scheduledCount?: number;
  completedRunnerCount?: number;
  createdCount: number;
  activatedCount: number;
  failedCount: number;
  completedCount: number;
  quotaExceededCount: number;
  blockedCount: number;
};

interface InstantUpdateDashboardProps {
  /** When true, render reporting content directly (no accordion) and load on mount. */
  embedded?: boolean;
}

export const InstantUpdateDashboard: React.FC<InstantUpdateDashboardProps> = ({ embedded = false }) => {
  Logger.debug('InstantUpdateDashboard.tsx', 'InstantUpdateDashboard: Component is rendering');
  const theme = useTheme();
  const navigate = useNavigate();
  const labels = useSelector((state: RootState) => selectLabels(state));
  const company = useSelector((state: RootState) => (state as any)?.authentication?.company);
  const [stats, setStats] = useState<DashboardStats>({
    totalDatasets: 0,
    activeDatasets: 0,
    totalTemplates: 0,
    totalCampaigns: 0,
  });
  const [analyticsSnapshot, setAnalyticsSnapshot] = useState<any>(null);
  const [templatesStats, setTemplatesStats] = useState<TemplatesStats | null>(null);
  const [datasetsStats, setDatasetsStats] = useState<DatasetsStats | null>(null);
  const [campaignsStats, setCampaignsStats] = useState<CampaignsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!!embedded);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await apiClient.get('/iu/analytics/dashboard-stats');
      const snapshot = res?.data?.snapshot ?? null;
      const tmpl = res?.data?.templatesStats ?? null;
      const ds = res?.data?.datasetsStats ?? null;
      const camp = res?.data?.campaignsStats ?? null;

      setAnalyticsSnapshot(snapshot);
      setTemplatesStats(tmpl);
      setDatasetsStats(ds);
      setCampaignsStats(camp);

      setStats({
        totalDatasets: ds?.total ?? 0,
        activeDatasets: ds?.active ?? 0,
        totalTemplates: tmpl?.total ?? 0,
        totalCampaigns: camp?.total ?? 0,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      Logger.error('InstantUpdateDashboard.tsx', `InstantUpdateDashboard: Failed to fetch data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!expanded) return;
    fetchDashboardData();
  }, [expanded]);

  // Helpers for Realtime Quota Gauges
  const getCurrentIUPlan = () => {
    try {
      const plans = (company as any)?.productPlans || [];
      if (!Array.isArray(plans) || plans.length === 0) return null;
      const now = new Date();
      // Prefer active plan within date range
      let plan = plans.find((p: any) => p.module === 'instantupdate.ai' && p.status === 'active'
        && (!p.startedAt || new Date(p.startedAt) <= now)
        && (!p.endsAt || new Date(p.endsAt) >= now));
      if (plan) return plan;
      // Fallback: latest plan for module
      const sorted = plans
        .filter((p: any) => p.module === 'instantupdate.ai')
        .sort((a: any, b: any) => new Date(b.endsAt || 0).getTime() - new Date(a.endsAt || 0).getTime());
      return sorted.length > 0 ? sorted[0] : null;
    } catch (_e) {
      return null;
    }
  };

  /** True when company has at least one product plan row for InstantUpdate (used for monthly credit panel). */
  const hasInstantUpdateProductPlan = () => {
    const plans = (company as any)?.productPlans;
    return Array.isArray(plans) && plans.some((p: any) => p?.module === 'instantupdate.ai');
  };

  /** Plan for gauges; prefers active/current window, else latest instantupdate.ai plan by endsAt. */
  const getPlanDataForInstantUpdateGauges = (): any | null => {
    const resolved = getCurrentIUPlan();
    if (resolved) return resolved;
    const raw = (company as any)?.productPlans || [];
    const iu = raw.filter((p: any) => p?.module === 'instantupdate.ai');
    if (iu.length === 0) return null;
    return iu.sort(
      (a: any, b: any) => new Date(b.endsAt || 0).getTime() - new Date(a.endsAt || 0).getTime(),
    )[0];
  };

  const QuotaGauge: React.FC<{
    title: string;
    used?: number;
    limit?: number;
    color: string;
    disabled?: boolean;
    disabledTooltip?: string;
  }> = ({ title, used = 0, limit = 0, color, disabled = false, disabledTooltip }) => {
    const safeUsed = Number(used) || 0;
    const safeLimit = Number(limit) || 0;
    const isUnlimited = !isFinite(safeLimit) || safeLimit <= 0;
    const percentage = !isUnlimited ? Math.min(100, Math.max(0, (safeUsed / safeLimit) * 100)) : 0;
    const gaugeColor = disabled ? theme.palette.action.disabled : color;
    const dashPanel = theme.palette.dashboard;
    const gaugeTextColor = dashPanel?.gaugePanelTextColor;
    const card = (
      <Card
        elevation={dashPanel?.gaugePanelBackgroundColor ? 0 : 1}
        sx={{
          position: 'relative',
          overflow: disabled ? 'hidden' : 'visible',
          minWidth: 0,
          width: '100%',
          borderRadius: 3,
          opacity: disabled ? 0.72 : 1,
          ...(dashPanel?.gaugePanelBackgroundColor
            ? {
                bgcolor: dashPanel.gaugePanelBackgroundColor,
                boxShadow: dashPanel.gaugePanelBoxShadow ?? 3,
              }
            : {
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
              }),
          border: (t) =>
            `1px solid ${
              t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : t.palette.grey[300]
            }`,
          ...(disabled ? { filter: 'grayscale(0.35)' } : {}),
          ...(gaugeTextColor ? { color: gaugeTextColor } : {}),
        }}
      >
        {disabled && (
          <Box
            sx={{
              position: 'absolute',
              top: 22,
              right: -36,
              zIndex: 2,
              width: 150,
              py: 0.35,
              textAlign: 'center',
              transform: 'rotate(45deg)',
              transformOrigin: 'center',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(183, 28, 28, 0.95)' : t.palette.error.main,
              color: (t) => t.palette.error.contrastText,
              pointerEvents: 'none',
              boxShadow: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 800,
                fontSize: '0.62rem',
                letterSpacing: 0.08,
                lineHeight: 1.25,
              }}
            >
              {'- DISABLED- '}
            </Typography>
          </Box>
        )}
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 0.5,
            }}
          >
            {(() => {
              const width = 140; // SVG width
              const height = 84; // SVG height (semi-circle)
              const cx = width / 2;
              const cy = height; // center at bottom to draw upper semicircle
              const r = 64;
              // Upper semicircle arc path from left to right
              const startX = cx - r;
              const startY = cy;
              const endX = cx + r;
              const endY = cy;
              // Needle angle: 0% => left (π), 100% => right (0), 50% => up (π/2)
              const theta = Math.PI * (1 - percentage / 100);
              const needleLen = r - 10;
              const needleX = cx + needleLen * Math.cos(theta);
              const needleY = cy - needleLen * Math.sin(theta);
              const lineY = height + 6;
              const svgH = lineY + 8;
              return (
                <svg
                  width={width}
                  height={svgH}
                  viewBox={`0 0 ${width} ${svgH}`}
                  aria-hidden
                  style={{ display: 'block', flexShrink: 0 }}
                >
                  {/* Base arc */}
                  <path
                    d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
                    fill="none"
                    stroke="rgba(128,128,128,0.3)"
                    strokeWidth="8"
                    pathLength={100}
                    strokeDasharray={isUnlimited && !disabled ? '6 8' : undefined}
                  />
                  {!isUnlimited && !disabled && (
                    <>
                      <path
                        d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
                        fill="none"
                        stroke={gaugeColor}
                        strokeWidth="8"
                        pathLength={100}
                        strokeDasharray={`${percentage} 100`}
                        strokeLinecap="round"
                      />
                      <line
                        x1={cx}
                        y1={cy}
                        x2={needleX}
                        y2={needleY}
                        stroke={gaugeColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <circle cx={cx} cy={cy} r="4" fill={gaugeColor} />
                    </>
                  )}
                  <line
                    x1={startX}
                    y1={lineY}
                    x2={endX}
                    y2={lineY}
                    stroke="rgba(128,128,128,0.3)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  {isUnlimited && !disabled && (
                    <text
                      x={cx}
                      y={lineY - 10}
                      textAnchor="middle"
                      fontSize="16"
                      fontWeight="700"
                      fill="currentColor"
                    >
                      {`${safeUsed}`}
                    </text>
                  )}
                  {disabled ? (
                    <text x={cx} y={cy - r + 20} textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor">
                      —
                    </text>
                  ) : !isUnlimited ? (
                    <text x={cx} y={cy - r + 20} textAnchor="middle" fontSize="11" fontWeight="700" fill="currentColor">
                      {`${Math.round(percentage)}%`}
                    </text>
                  ) : (
                    <text x={cx} y={cy - r + 20} textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor">
                      ∞
                    </text>
                  )}
                </svg>
              );
            })()}
            <Typography
              component="div"
              variant="subtitle2"
              align="center"
              sx={{
                fontWeight: 700,
                mt: 1,
                px: 0.5,
                width: '100%',
                minWidth: 0,
                lineHeight: 1.35,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                hyphens: 'auto',
                color: gaugeTextColor ?? 'text.primary',
              }}
            >
              {title}
            </Typography>
            <Typography
              component="div"
              variant="caption"
              align="center"
              sx={{
                mt: 0.5,
                px: 0.5,
                width: '100%',
                minWidth: 0,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                color: gaugeTextColor ?? 'text.secondary',
              }}
            >
              {disabled ? 'Not available' : `${safeUsed}/${isUnlimited ? 'Unlimited' : safeLimit}`}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
    return disabled && disabledTooltip ? (
      <Tooltip title={disabledTooltip} placement="top" arrow>
        <Box component="span" sx={{ display: 'block', width: '100%' }}>
          {card}
        </Box>
      </Tooltip>
    ) : (
      card
    );
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    onClick?: () => void;
    textColor?: string;
    /** PROFESSIONAL light: Total Templates / DataSets — dark metric + icon. */
    usePrimaryMetricStyle?: boolean;
  }> = ({ title, value, icon, color, subtitle, onClick, textColor, usePrimaryMetricStyle }) => {
    const dashPanel = theme.palette.dashboard;
    const statBg = dashPanel?.statPanelBackgroundColor;
    const statLabelColor = dashPanel?.statPanelTextColor;
    const primaryMetricColor = usePrimaryMetricStyle ? dashPanel?.statPanelPrimaryMetricColor : undefined;
    const primaryIconBg = usePrimaryMetricStyle ? dashPanel?.statPanelPrimaryIconBg : undefined;
    const valueColor = textColor || primaryMetricColor || color;
    return (
    <Card
      elevation={statBg ? 0 : 1}
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        borderRadius: 3,
        ...(statBg
          ? {
              bgcolor: statBg,
              boxShadow: dashPanel?.statPanelBoxShadow ?? 3,
            }
          : {
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgb(20, 25, 55)'
                : theme.palette.grey[100],
            }),
        border: `1px solid ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.12)' 
          : theme.palette.grey[300]}`,
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          ...(statBg
            ? { bgcolor: statBg }
            : {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgb(30, 35, 65)'
                  : theme.palette.grey[200],
              }),
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: valueColor }}>
              {value.toLocaleString()}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: statLabelColor ?? 'text.secondary' }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: statLabelColor ?? 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{
            bgcolor: primaryIconBg ?? color,
            color: primaryMetricColor ?? theme.palette.getContrastText(color),
            width: 56,
            height: 56,
            '& .MuiSvgIcon-root': {
              color: primaryMetricColor ?? 'inherit',
            },
          }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
    );
  };

  const accordionLeftColor = theme.palette.dashboard?.welcomePanelLeftTextColor
    ?? (theme.palette.mode === 'light' ? 'black' : 'inherit');
  const accordionRightColor = theme.palette.dashboard?.welcomePanelRightTextColor
    ?? (theme.palette.mode === 'dark' ? 'black' : 'inherit');

  const body = (
    <>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {expanded && !loading && !error && (
        <>
      {/* Daily credit gauges: resolved InstantUpdate plan (active / in window, else latest) */}
      {(() => {
        const plan = getCurrentIUPlan();
        if (!plan) return null;
        const planWhatsapp = (plan as any)?.whatsApp || {};
        const planEmail = (plan as any)?.email || {};
        const planTelegram = (plan as any)?.telegram || {};
        const planGsmText = (plan as any)?.gsmText || {};
        const gsmWorkerName = String((plan as any)?.gsmWorkerName ?? '').trim();
        const gsmQuotaDisabled =
          (plan as any)?.module === 'instantupdate.ai' && !gsmWorkerName;
        const gaugeGridSx = {
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr',
            lg: '1fr 1fr 1fr 1fr',
          },
          '& > *': { minWidth: 0 },
        } as const;
        return (
          <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Realtime Daily Credit Usage
              </Typography>
              <Box sx={gaugeGridSx}>
                <QuotaGauge
                  title="WhatsApp Daily Credit"
                  used={planWhatsapp?.perDayTracker ?? 0}
                  limit={planWhatsapp?.perDayLimit}
                  color={theme.palette.success.main}
                />
                <QuotaGauge
                  title="Email Daily Credit"
                  used={planEmail?.perDayTracker ?? 0}
                  limit={planEmail?.perDayLimit}
                  color={theme.palette.warning.main}
                />
                <QuotaGauge
                  title="Telegram Daily Credit"
                  used={planTelegram?.perDayTracker ?? 0}
                  limit={planTelegram?.perDayLimit}
                  color={theme.palette.info.main}
                  disabled
                  disabledTooltip="Telegram quota tracking is not enabled for this product yet."
                />
                <QuotaGauge
                  title="SMS Daily Credit"
                  used={planGsmText?.perDayTracker ?? 0}
                  limit={planGsmText?.perDayLimit}
                  color={theme.palette.secondary.main}
                  disabled={gsmQuotaDisabled}
                  disabledTooltip={
                    gsmQuotaDisabled
                      ? 'GSM text quota applies when a GSM worker is assigned to your instantupdate.ai plan.'
                      : undefined
                  }
                />
              </Box>
          </Box>
        );
      })()}

      {/* Monthly credit: show when company has an instantupdate.ai product plan row */}
      {(() => {
        if (!hasInstantUpdateProductPlan()) return null;
        const plan = getPlanDataForInstantUpdateGauges();
        if (!plan) return null;
        const planWhatsapp = (plan as any)?.whatsApp || {};
        const planEmail = (plan as any)?.email || {};
        const planTelegram = (plan as any)?.telegram || {};
        const planGsmText = (plan as any)?.gsmText || {};
        const gsmWorkerName = String((plan as any)?.gsmWorkerName ?? '').trim();
        const gsmMonthlyDisabled =
          (plan as any)?.module === 'instantupdate.ai' && !gsmWorkerName;
        const gaugeGridSx = {
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr',
            lg: '1fr 1fr 1fr 1fr',
          },
          '& > *': { minWidth: 0 },
        } as const;
        return (
          <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Realtime Monthly Credit Usage
              </Typography>
              <Box sx={gaugeGridSx}>
                <QuotaGauge
                  title="WhatsApp Monthly Credit"
                  used={planWhatsapp?.totalCreditTracker ?? 0}
                  limit={planWhatsapp?.totalCredit}
                  color={theme.palette.success.main}
                />
                <QuotaGauge
                  title="Email Monthly Credit"
                  used={planEmail?.totalCreditTracker ?? 0}
                  limit={planEmail?.totalCredit}
                  color={theme.palette.warning.main}
                />
                <QuotaGauge
                  title="Telegram Monthly Credit"
                  used={planTelegram?.totalCreditTracker ?? 0}
                  limit={planTelegram?.totalCredit}
                  color={theme.palette.info.main}
                  disabled
                  disabledTooltip="Telegram monthly credit tracking is not enabled for this product yet."
                />
                <QuotaGauge
                  title="SMS Monthly Credit"
                  used={planGsmText?.totalCreditTracker ?? 0}
                  limit={planGsmText?.totalCredit}
                  color={theme.palette.secondary.main}
                  disabled={gsmMonthlyDisabled}
                  disabledTooltip={
                    gsmMonthlyDisabled
                      ? 'SMS monthly credit applies when a GSM worker is assigned to your instantupdate.ai plan.'
                      : undefined
                  }
                />
              </Box>
          </Box>
        );
      })()}

      <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            {labels?.['modules-instantupdate-dashboard-main-title'] ?? 'modules-instantupdate-dashboard-main-title'}
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Tooltip
                title={
                  templatesStats
                    ? `WhatsApp: ${templatesStats.whatsApp.toLocaleString()} | SMS: ${templatesStats.sms.toLocaleString()} | Email: ${templatesStats.email.toLocaleString()} | Telegram: ${templatesStats.telegram.toLocaleString()}`
                    : ''
                }
                disableTouchListener
                arrow
                placement="top"
              >
                <Box>
                  <StatCard
                    title={labels?.['modules-instantupdate-dashboard-total-templates'] ?? 'Total Templates'}
                    value={stats.totalTemplates}
                    icon={<DescriptionIcon />}
                    color={theme.palette.secondary.main}
                    usePrimaryMetricStyle
                    onClick={() => navigate('/instant-updates/templates')}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Tooltip
                title={
                  datasetsStats
                    ? `ACTIVE: ${datasetsStats.active.toLocaleString()} | INACTIVE: ${datasetsStats.inactive.toLocaleString()} | DELETED: ${datasetsStats.deleted.toLocaleString()}`
                    : ''
                }
                disableTouchListener
                arrow
                placement="top"
              >
                <Box>
                  <StatCard
                    title={labels?.['modules-instantupdate-dashboard-total-datasets'] ?? 'Total DataSets'}
                    value={stats.totalDatasets}
                    icon={<StorageIcon />}
                    color={theme.palette.secondary.main}
                    usePrimaryMetricStyle
                    onClick={() => navigate('/instant-updates/data-sets')}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Tooltip
                title={
                  campaignsStats
                    ? `CampaignRunners collection: ${campaignsStats.total.toLocaleString()} total, ${(campaignsStats.scheduledCount ?? 0).toLocaleString()} scheduled, ${(campaignsStats.completedRunnerCount ?? 0).toLocaleString()} completed. Latest analytics snapshot — Created: ${campaignsStats.createdCount.toLocaleString()} | Activated: ${campaignsStats.activatedCount.toLocaleString()} | Failed: ${campaignsStats.failedCount.toLocaleString()} | Completed: ${campaignsStats.completedCount.toLocaleString()} | Quota Exceeded: ${campaignsStats.quotaExceededCount.toLocaleString()} | Blocked: ${campaignsStats.blockedCount.toLocaleString()}`
                    : ''
                }
                disableTouchListener
                arrow
                placement="top"
              >
                <Box>
                  <StatCard
                    title={labels?.['modules-instantupdate-dashboard-total-campaigns'] ?? 'Total Campaigns'}
                    value={stats.totalCampaigns}
                    icon={<CampaignIcon />}
                    color={theme.palette.success.main}
                    onClick={() => navigate('/instant-updates/runner')}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <StatCard
                title={labels?.['modules-instantupdate-dashboard-total-completed'] ?? 'Total Completed'}
                value={campaignsStats?.completedRunnerCount ?? 0}
                icon={<TaskAltIcon />}
                color={theme.palette.info.main}
                onClick={() => navigate('/instant-updates/runner')}
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <StatCard
                title={labels?.['modules-instantupdate-dashboard-total-scheduled'] ?? 'Total Scheduled'}
                value={campaignsStats?.scheduledCount ?? 0}
                icon={<ScheduleIcon />}
                color={theme.palette.warning.main}
                onClick={() => navigate('/instant-updates/runner')}
              />
            </Box>
          </Box>
      </Box>

      {/* Graphs - Row 1: max 2 per row, stacked on small */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: '1fr 1fr',
            lg: '1fr 1fr',
            xl: '1fr 1fr',
          },
          mb: 3,
        }}
      >
        <Box>
          <TemplatesCreationGraph />
        </Box>
        <Box>
          <DatasetsCreationGraph />
        </Box>
        <Box>
          <CampaignsCreationGraph />
        </Box>
        <Box>
          <SchedulerStatsGraph />
        </Box>
      </Box>

      {/* Graphs - Row 2: 2 columns on medium+, stacked on small */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: '1fr 1fr',
            lg: '1fr 1fr',
          },
        }}
      >
        <Box>
          <WhatsappCampaignSummaryGraph />
        </Box>
        <Box>
          <EmailCampaignSummaryGraph />
        </Box>
      </Box>
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ px: 1, pb: 1 }}>{body}</Box>;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      sx={{ mb: 0, borderRadius: '12px !important', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: accordionRightColor }} />}
        sx={{
          px: 3,
          py: 1,
          borderRadius: '12px',
          background: theme.palette.dashboard?.welcomePanelGradient
            ?? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: accordionLeftColor }}>
          Instant Update Dashboard
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        {body}
      </AccordionDetails>
    </Accordion>
  );
}; 